import React, { useMemo, useState } from 'react';
import { SxProps } from '@mui/system';
import { BaseEventPayload, ElementDragType } from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types.js';
import { Box } from '@mui/material';
import RejectBox from '../RejectBox';
import Header from '../Header';
import EntityHeader from '../EntityHeader';
import Column from './Column';
import { ListLoadingDecorator, useListContext } from 'framework/list';
import ExpandButton from '../ExpandButton';
import { UiThemeOverrider } from 'framework/uiTheme';
import { ShowContextProvider, ShowData } from 'framework/show';
import { ListFieldLayout } from 'framework/ui/layout';
import { Alert } from 'framework/ui/mui-builder';
import ContextMenu, { ContextMenuItem } from '../contextMenu/ContextMenu';
import { useDragMonitor } from 'framework/dnd';
import useRecruitingBoardService, { REJECT_BOX_ID } from '../../useRecruitingBoardService';
import { MappedApplication, VacancyItem } from '../../types';
import { columnNames } from '../../constants';

const FIRST_COLUMN_WIDTH = 170;
const COLUMN_WIDTH = 200;
const GAP = 1.5;
interface BoardProps {
  sx?: SxProps;
}

const Board = ({ sx = {} }: BoardProps) => {
  const [activeNode, setActiveNode] = useState<any>(null);
  const [expandedSectionIds, setExpandedSectionIds] = useState<string[]>([]);
  const { data: { items = [] } = {}, loading = true } = useListContext();
  const { moveVacancyGroup } = useRecruitingBoardService();
  const menuItems = useMemo(
    () => [
      {
        label: 'Change vacancy group',
        onClick: (application: MappedApplication, vacancyGroup: VacancyItem) => {
          moveVacancyGroup(application, vacancyGroup?.id, vacancyGroup?.alias);
        },
        submenu: items.map(({ name, ...rest }) => ({
          ...rest,
          label: name,
        })),
      },
    ],
    [items, moveVacancyGroup],
  ) as ContextMenuItem[];

  const handleDragStart = (event: BaseEventPayload<ElementDragType>) => {
    setActiveNode(event.source.data);
  };

  const onDragEnd = (event: BaseEventPayload<ElementDragType>) => {
    setActiveNode(null);
  };

  const onSectionToggle = (sectionId: string) => {
    setExpandedSectionIds(prev =>
      prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId],
    );
  };

  useDragMonitor({
    onDrop: onDragEnd,
    onDragStart: handleDragStart,
  });

  return (
    <UiThemeOverrider
      overriddenLayouts={{
        BaseFieldLayout: ListFieldLayout,
      }}
    >
      <ListLoadingDecorator items={items} loading={loading}>
        {items.length ? (
          <Box
            sx={{
              overflowX: 'auto',
              overflowY: 'auto',
              maxWidth: '100%',
              maxHeight: '100%',
              paddingBottom: GAP,
              '@supports (-webkit-hyphens: none)': { maxHeight: '80vh' },
              ...sx,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `${FIRST_COLUMN_WIDTH}px repeat(${columnNames.length}, ${COLUMN_WIDTH}px)`,
                gap: GAP,
                paddingBottom: GAP,
                position: 'sticky',
                top: 0,
                zIndex: 101,
                backgroundColor: 'background.default',
                width: 'fit-content',
              }}
            >
              <Header data={items} />
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `${FIRST_COLUMN_WIDTH}px repeat(${columnNames.length}, ${COLUMN_WIDTH}px)`,
                columnGap: GAP,
                width: 'fit-content',
              }}
            >
              {items.map((entity: any) => (
                <React.Fragment key={entity.id}>
                  <ShowContextProvider
                    value={{
                      data: new ShowData(entity),
                    }}
                  >
                    <EntityHeader />

                    <ContextMenu menuItems={menuItems}>
                      <>
                        {columnNames.map(columnSettings => (
                          <Column
                            key={`${entity.id}-${columnSettings.name}`}
                            entity={entity}
                            expanded={expandedSectionIds.includes(entity.id)}
                            columnSettings={columnSettings}
                            items={entity?.items?.filter((task: any) => task.stage === columnSettings.stage)}
                          />
                        ))}
                      </>
                    </ContextMenu>
                    {columnNames.some(columnSettings => {
                      return entity?.items?.filter((task: any) => task.stage === columnSettings.stage).length > 4;
                    }) ? (
                      <ExpandButton
                        expanded={expandedSectionIds.includes(entity.id)}
                        onClick={() => onSectionToggle(entity.id)}
                      />
                    ) : (
                      /* Vacancies divider */
                      <Box sx={{ height: 10 * GAP, gridColumn: '1 / -1' }} />
                    )}
                  </ShowContextProvider>
                </React.Fragment>
              ))}
            </Box>

            <RejectBox id={REJECT_BOX_ID} activeNode={activeNode} />
          </Box>
        ) : (
          <Alert color="info" sx={{ mt: 1.5 }}>
            No records found
          </Alert>
        )}
      </ListLoadingDecorator>
    </UiThemeOverrider>
  );
};

export default Board;
